import type { fabric } from 'fabric';
import type { Rect } from './authored-bundles-bbox';
import type { MatchLayoutAxis, MatchLayoutSettings } from './match-layout-settings';

export interface MatchBundle {
  /** Slot/row index the bundle is addressed by — `matchNumber` for match
   *  templates, `teamNumber` (table position) for standings templates. */
  slot: number;
  members: readonly fabric.Object[];
  bbox: Rect;
}

export interface MatchLayoutSnapshot {
  positions: Map<fabric.Object, { left: number; top: number }>;
}

export interface ApplyMatchLayoutArgs {
  surviving: readonly MatchBundle[];
  authoredRegion: Rect;
  settings: MatchLayoutSettings;
}

export const EMPTY_SNAPSHOT: MatchLayoutSnapshot = { positions: new Map() };

/**
 * Translate every surviving match bundle along `axis` so the surviving group
 * sits inside `authoredRegion` per `alignment`. Cross-axis position is
 * preserved. Caller must discard any ActiveSelection before invoking so
 * `obj.left/top` are canvas-absolute (see project CLAUDE.md gotcha on
 * ActiveSelection-relative coords).
 *
 * Returns a snapshot of the prior {left, top} of every moved object so
 * editor preview can revert via `revertMatchLayout`. Render-time callers
 * can discard it.
 */
export function applyMatchLayout(args: ApplyMatchLayoutArgs): MatchLayoutSnapshot {
  const { surviving, authoredRegion, settings } = args;
  if (!settings.enabled || surviving.length === 0) return EMPTY_SNAPSHOT;

  const axis = resolveAxis(settings.axis, authoredRegion);
  const snapshot: MatchLayoutSnapshot = { positions: new Map() };

  if (settings.alignment === 'distribute' && surviving.length >= 2) {
    applyDistribute(surviving, authoredRegion, axis, snapshot);
  } else {
    applyUniformTranslate(surviving, authoredRegion, axis, settings.alignment, snapshot);
  }

  return snapshot;
}

export function revertMatchLayout(snapshot: MatchLayoutSnapshot): void {
  snapshot.positions.forEach((pos, obj) => {
    obj.set({ left: pos.left, top: pos.top });
    obj.setCoords();
  });
}

function resolveAxis(axis: MatchLayoutAxis, region: Rect): 'vertical' | 'horizontal' {
  if (axis === 'vertical' || axis === 'horizontal') return axis;
  return region.height >= region.width ? 'vertical' : 'horizontal';
}

function applyUniformTranslate(
  surviving: readonly MatchBundle[],
  region: Rect,
  axis: 'vertical' | 'horizontal',
  alignment: 'center' | 'start' | 'end' | 'distribute',
  snapshot: MatchLayoutSnapshot,
): void {
  const survivingUnion = unionBundleBboxes(surviving);
  const delta = axisDelta(survivingUnion, region, axis, alignment);
  if (delta === 0) return;
  const dx = axis === 'horizontal' ? delta : 0;
  const dy = axis === 'vertical' ? delta : 0;
  for (const bundle of surviving) translateMembers(bundle.members, dx, dy, snapshot);
}

/**
 * «Space between», as `justify-content: space-between`: the first bundle starts at the region's start, the last ends
 * at its end, the gaps between bundles are equal. Placing bundle *centres* on the region's ends instead pushed half of
 * the first row over the header above it and half of the last one below the region.
 */
function applyDistribute(
  surviving: readonly MatchBundle[],
  region: Rect,
  axis: 'vertical' | 'horizontal',
  snapshot: MatchLayoutSnapshot,
): void {
  const sorted = [...surviving].sort(bySlot);
  const n = sorted.length;
  const regionStart = axis === 'vertical' ? region.top : region.left;
  const regionSize = axis === 'vertical' ? region.height : region.width;
  const sizeOf = (bundle: MatchBundle) => (axis === 'vertical' ? bundle.bbox.height : bundle.bbox.width);
  const gap = (regionSize - sorted.reduce((sum, bundle) => sum + sizeOf(bundle), 0)) / (n - 1);
  let cursor = regionStart;
  for (let i = 0; i < n; i++) {
    const bundle = sorted[i];
    const currentStart = axis === 'vertical' ? bundle.bbox.top : bundle.bbox.left;
    const delta = cursor - currentStart;
    cursor += sizeOf(bundle) + gap;
    if (delta === 0) continue;
    const dx = axis === 'horizontal' ? delta : 0;
    const dy = axis === 'vertical' ? delta : 0;
    translateMembers(bundle.members, dx, dy, snapshot);
  }
}

function unionBundleBboxes(bundles: readonly MatchBundle[]): Rect {
  let minL = Infinity;
  let minT = Infinity;
  let maxR = -Infinity;
  let maxB = -Infinity;
  for (const b of bundles) {
    if (b.bbox.left < minL) minL = b.bbox.left;
    if (b.bbox.top < minT) minT = b.bbox.top;
    if (b.bbox.left + b.bbox.width > maxR) maxR = b.bbox.left + b.bbox.width;
    if (b.bbox.top + b.bbox.height > maxB) maxB = b.bbox.top + b.bbox.height;
  }
  return { left: minL, top: minT, width: maxR - minL, height: maxB - minT };
}

function axisDelta(
  surviving: Rect,
  region: Rect,
  axis: 'vertical' | 'horizontal',
  alignment: 'center' | 'start' | 'end' | 'distribute',
): number {
  const sStart = axis === 'vertical' ? surviving.top : surviving.left;
  const sEnd = axis === 'vertical' ? surviving.top + surviving.height : surviving.left + surviving.width;
  const rStart = axis === 'vertical' ? region.top : region.left;
  const rEnd = axis === 'vertical' ? region.top + region.height : region.left + region.width;
  switch (alignment) {
    case 'start':
      return rStart - sStart;
    case 'end':
      return rEnd - sEnd;
    case 'center':
    case 'distribute':
    default: {
      const sCenter = (sStart + sEnd) / 2;
      const rCenter = (rStart + rEnd) / 2;
      return rCenter - sCenter;
    }
  }
}

function translateMembers(
  members: readonly fabric.Object[],
  dx: number,
  dy: number,
  snapshot: MatchLayoutSnapshot,
): void {
  for (const obj of members) {
    if (!snapshot.positions.has(obj)) {
      snapshot.positions.set(obj, { left: obj.left ?? 0, top: obj.top ?? 0 });
    }
    obj.set({ left: (obj.left ?? 0) + dx, top: (obj.top ?? 0) + dy });
    obj.setCoords();
  }
}

function bySlot(a: MatchBundle, b: MatchBundle): number {
  return a.slot - b.slot;
}
